<template>
  <v-container class="fill-height d-flex align-center justify-center">
    <v-card
      v-if="isCheckingAuth"
      class="pa-8 text-center"
      max-width="400px"
      width="100%"
      elevation="8"
    >
      <v-progress-circular indeterminate color="primary" size="64" class="mb-4" />
      <div class="text-h6 mb-2">Checking Authentication</div>
      <div class="text-body-2 text-medium-emphasis">
        Please wait while we verify your session...
      </div>
    </v-card>

    <v-card v-else-if="!isAuthenticated" class="pa-6" max-width="500px" width="100%" elevation="8">
      <div class="text-center mb-6">
        <v-icon size="64" color="primary" class="mb-4">mdi-shield-link-variant</v-icon>
        <v-card-title class="text-h4 pa-0 mb-2">Single Sign-On</v-card-title>
        <v-card-subtitle class="text-body-1">
          Sign in to continue to
          <v-chip color="primary" variant="tonal" size="small" class="ml-1">
            {{ clientAppName || "application" }}
          </v-chip>
        </v-card-subtitle>
      </div>
      <v-card-text class="pa-0">
        <LoginForm @login-success="handleLoginSuccess" />
      </v-card-text>
    </v-card>

    <v-card v-else class="pa-8 text-center" max-width="400px" width="100%" elevation="8">
      <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
      <div class="text-h6 mb-2">Authenticated Successfully</div>
      <div class="text-body-2 text-medium-emphasis mb-4">
        Redirecting you to
        <v-chip color="primary" variant="tonal" size="small" class="ml-1">
          {{ clientAppName || "application" }}
        </v-chip>
      </div>
      <v-progress-linear indeterminate color="primary" class="mb-2"></v-progress-linear>
      <div class="text-caption text-medium-emphasis">This will only take a moment...</div>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { getBearerToken } from "@/utils";
import { URLs } from "@/constants";
import { IApp } from "@/types";
import LoginForm from "@/components/Auth/LoginForm.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const callbackUrl = computed(() => (route.query.callback_url as string) || "");
const clientAppName = computed(() => (route.query.app_name as string) || "Thor Hyperlocal");

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isCheckingAuth = ref(true);

onMounted(async () => {
  if (!callbackUrl.value) {
    router.push(URLs.PAGE_AUTH_LOGIN);
    return;
  }

  const token = getBearerToken();

  if (token && authStore.status === IApp.ILoadingStatus.Idle) {
    await authStore.getUserInfo();
  }

  isCheckingAuth.value = false;

  if (isAuthenticated.value && callbackUrl.value) {
    setTimeout(() => redirectWithToken(), 500);
  }
});

const handleLoginSuccess = async () => {
  if (callbackUrl.value) {
    redirectWithToken();
  }
};

const redirectWithToken = () => {
  const token = getBearerToken();
  if (!token) {
    return;
  }

  const url = new URL(callbackUrl.value);
  url.searchParams.set("token", token);
  url.searchParams.set("success", "true");

  window.location.href = url.toString();
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>
